import { DataStore } from "aws-amplify/datastore";
import orderBy from "lodash-es/orderBy.js";
import cloneDeep from "lodash-es/cloneDeep.js";
import { ModificationAction, PlaceReport, PlaceMediaItem } from "../../../models";
import { Place } from "../../../models";
import {
    compiledFilterPlace,
    compiledFilterPlaceAsync,
    compileFilters,
    IFilter,
} from "../../Filter";
import {
    IPlaceMediaItem,
    IPlace,
    IPlaceReport,
    awsToSimpleObject,
    IMedia,
} from "../../models/Models";
import { IModification, JournalTable } from "./JournalTable";
import { ModificationModel } from "../../../models";
import { reverseGeocode } from "../../Geocode";
import { elevationAtLocation } from "../../Elevation";
import { IPlaceFilter } from "./PlaceFilterTable";
import { DataStoreTable } from "./DataStoreTable";
import fluent, { FluentIterable } from "fluent-iterable";
import { DataProviderArgs } from "../../DataStore/DataProvider";
import { ChangeSet } from "../../DataStore/ChangeSet";
import { Kernel } from "../../Kernel";
import { getPlaceDiveRating } from "../../PlaceUtils";
import { awsMediaInfoToSimpleMediaInfo, PlaceMediaTable } from "./PlaceMediaTable";
import { PlaceReportTable } from "./PlaceReportTable";
import {
    awsPlaceReportToSimplePlaceReport,
    awsPlaceToSimplePlace,
    IPlaceTable,
    simpleLocationToAwsLocation,
    simplePlaceToAwsPlace,
} from "../IPlaceTable";

export type Country = {
    name: string;
    states: Set<string>;
};

export type PlacesChangedEvent = {
    places: FluentIterable<IPlace>;

    // If this is true the update was triggered by the filter changing
    filtered: boolean;
};

export class PlaceTable extends DataStoreTable<IPlace> implements IPlaceTable {
    private _filteredCount = 0;
    private _countries: Country[] = [];

    constructor(
        args: DataProviderArgs,
        private _placeMediaTable: PlaceMediaTable,
        private _placeReportTable: PlaceReportTable
    ) {
        super(
            args,
            "place",
            Place,
            (obj: Place) => awsPlaceToSimplePlace(obj),
            (obj: IPlace) => simplePlaceToAwsPlace(obj)
        );
    }

    protected override async _notifyListeners(changeSet: ChangeSet<IPlace>) {
        super._notifyListeners(changeSet);
        this._updateCountriesAndStates();
    }

    get filteredCount(): number {
        return this._filteredCount;
    }

    get countries(): Country[] {
        return this._countries;
    }

    public async filteredItems(filter: IFilter): Promise<[Map<string, IPlace>, number]> {
        const filteredPlaces = new Map<string, IPlace>();
        let count = 0;
        for await (const place of this._filteredItemsGenerator(filter)) {
            filteredPlaces.set(place.id, place);
            ++count;
        }
        return [filteredPlaces, count];
    }

    public async filteredItemsCount(filter: IFilter) {
        let count = 0;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for await (const _ of this._filteredItemsGenerator(filter)) {
            ++count;
        }
        return count;
    }

    public async *_filteredItemsGenerator(filter: IFilter) {
        const compiledFilter = compileFilters(filter);
        for (const place of this.allSync()) {
            if (!(await compiledFilterPlaceAsync(compiledFilter, place))) {
                yield place;
            }
        }
    }

    private _updateCountriesAndStates() {
        const countries = new Map<string, Country>();
        for (const p of this.allSync()) {
            const c = p.data.address?.country ?? "Unknown";
            const s = p.data.address?.state ?? "Unknown";

            if (!countries.has(c)) {
                countries.set(c, { name: c, states: new Set<string>([s]) });
            } else {
                const ec = countries.get(c) as Country;
                if (!ec.states.has(s)) {
                    ec.states.add(s);
                }
            }
        }
        this._countries = orderBy([...countries.values()], "name");
    }

    public allFilteredItems(filter: IPlaceFilter): FluentIterable<IPlace> {
        const cp = compileFilters(filter.filter);
        return fluent(this.allSync()).filter((p) => !compiledFilterPlace(cp, p));
    }

    private async _addToJournal(
        action: ModificationAction,
        place: IPlace,
        contributor: string | undefined = undefined
    ) {
        contributor = contributor ?? Kernel.instance().user?.id;
        const journals = this._store.getSource("journals") as JournalTable;
        await journals.add(
            {
                action: action,
                model: ModificationModel.PLACE,
                modelId: place.id,
                modelName: place.data.name,
                detail: "",
            } as IModification,

            place.groupAccess ?? [],
            place.userAccess ?? [],
            contributor,
            place.caveCollection,
            place.scubaCollection
        );
    }

    public override async create(place: IPlace): Promise<IPlace> {
        const aws = awsToSimpleObject(place, ["reports", "ratings", "id"]);
        aws.data.location = simpleLocationToAwsLocation(place.data.location);
        aws.data.address = await reverseGeocode(place.data.location);
        aws.data.altitude = await elevationAtLocation(place.data.location);
        delete (aws as any).id;
        const savedPlace = awsPlaceToSimplePlace(await DataStore.save(new Place(aws)));
        await this._addToJournal(ModificationAction.CREATE, savedPlace);
        return savedPlace;
    }

    public override async update(
        place: IPlace,
        addToJournal = true,
        contributor: string | undefined = undefined
    ): Promise<IPlace | undefined> {
        const current = await this.read(place.id);
        if (current) {
            // do an update before we start waiting on the reverse geocode and elevation
            this._optimisticUpdateItem(place);

            // if the location has changed we need to reverse geocode again
            if (
                current.data.location.lat !== place.data.location.lat ||
                current.data.location.lon !== place.data.location.lon ||
                !place.data.altitude ||
                !place.data.address
            ) {
                place.data.address = await reverseGeocode(place.data.location);
                place.data.altitude = await elevationAtLocation(place.data.location);
            }
            const savedPlace = await super.update(place);

            if (addToJournal) {
                await this._addToJournal(ModificationAction.UPDATE, place, contributor);
            }
            return savedPlace;
        }
        return undefined;
    }

    public override async delete(id: string) {
        const toDelete = await DataStore.query(Place, id);
        if (toDelete) {
            const updated = Place.copyOf(toDelete, (updated) => {
                updated.softDeleted = "true";
            });
            await DataStore.save(updated);
            await this._addToJournal(ModificationAction.DELETE, toDelete as unknown as IPlace);
        }
    }

    async obliterate(id: string) {
        const todelete = await DataStore.query(Place, id);
        if (todelete) {
            await DataStore.delete(todelete);
        }
    }

    //-------------------------------------------------------------------------
    // Reports
    //-------------------------------------------------------------------------

    public async getReports(placeId: string): Promise<IPlaceReport[]> {
        const place = await DataStore.query(Place, placeId);
        if (place) {
            const reports = (await place.reports.toArray()).map((x: PlaceReport) =>
                awsPlaceReportToSimplePlaceReport(x)
            );
            return orderBy(reports, "date", "desc");
        }

        return [];
    }

    public async createReport(placeId: string, report: IPlaceReport): Promise<IPlaceReport> {
        const awsObject = cloneDeep(report) as any;
        awsObject.owner = Kernel.instance().user?.id;
        awsObject.place = await DataStore.query(Place, placeId);
        return awsPlaceReportToSimplePlaceReport(await DataStore.save(new PlaceReport(awsObject)));
    }

    public async deleteReport(id: string) {
        await this._placeReportTable.delete(id);
    }

    public async updateReport(item: IPlaceReport) {
        return await this._placeReportTable.update(item);
    }

    //-------------------------------------------------------------------------
    // Media
    //-------------------------------------------------------------------------

    public async getMediaItems(placeId: string): Promise<IPlaceMediaItem[]> {
        const place = await DataStore.query(Place, placeId);
        if (place) {
            return orderBy(await place.media.toArray(), "createdAt", "desc").map(
                (x: PlaceMediaItem) => awsMediaInfoToSimpleMediaInfo(x)
            );
        }

        return [];
    }

    public async createMediaItem(
        placeId: string,
        media: IMedia,
        groupAccess: string[]
    ): Promise<IPlaceMediaItem> {
        const awsObject = {
            media: cloneDeep(media) as any,
            owner: Kernel.instance().user?.id,
            placeMediaId: placeId,
            groupAccess,
        } as Omit<IPlaceMediaItem, "id">;

        return await this._placeMediaTable.create(awsObject);
    }

    public async deleteMediaItem(id: string) {
        await this._placeMediaTable.delete(id);
    }

    public async updateMediaItem(item: IPlaceMediaItem) {
        return await this._placeMediaTable.update(cloneDeep(item));
    }

    //-------------------------------------------------------------------------
    // Batch operations
    //-------------------------------------------------------------------------

    public async setElevationOnAll() {
        const all = await this.all();
        if (!all) {
            return;
        }

        for (const p of all) {
            if (p.data.locationKnown && !p.data.altitude) {
                try {
                    const e = await elevationAtLocation(p.data.location);
                    if (e) {
                        const np = cloneDeep(p);
                        np.data.altitude = e;
                        await this.update(np, false);
                        this._log.debug(`${np.data.name} : ${np.data.altitude} : ${np.id}`);
                    }
                } catch (error: any) {
                    this._log.error(error.toString());
                }
            }
        }
    }

    public async setAddressAll(forceUpdate: boolean) {
        const all = await this.all();
        if (!all) {
            return;
        }

        for (const p of all) {
            if (p.data.locationKnown && (forceUpdate || !p.data.address)) {
                try {
                    const e = await reverseGeocode(p.data.location);
                    if (e) {
                        const np = cloneDeep(p);
                        np.data.address = e;
                        await this.update(np, false);
                        this._log.info(`${np.data.name} : ${np.data.altitude} : ${np.id}`);
                    }
                } catch (error: any) {
                    this._log.error(error);
                }
            }
        }
    }
    /*
    public async fixDeletedAndCollection() {
        const all = await this.all();
        if (!all) {
            return;
        }

        this._log.info("Fixing deleted and collections");
        for (const p of all) {
            const np = cloneDeep(p);
            let changed = false;
            if (!np.softDeleted) {
                np.softDeleted = p.deleted ? "true" : "false";
            }

            if (!np.collections) {
                np.caveCollection = "true";
                changed = true;
            } else if (!np.caveCollection) {
                np.caveCollection = np.collections.includes("c") ? "true" : "false";
                changed = true;
            }
            if (!np.scubaCollection) {
                np.scubaCollection =
                    np.collections?.includes("s") || getPlaceDiveRating(p) ? "true" : "false";
                changed = true;
            }

            if (changed) {
                await this.update(np, false);
            }
        }
        this._log.info("Done fixing deleted and collections");
    }*/
}
